import { useEffect, useState } from 'react'
import { ApiOutlet } from '@vend/types'
import { useRegisterManager } from '../../../../../../ng-compat/hooks'
import { outletsStore } from '../../../../../../factories/database/outlets-store'
import { OutletOption } from '../../OutletSelect/OutletSelect.types'

type UseCrossLocationFulfilmentOutletOptionsParams = {
  shouldLoadOutletOptions: boolean
}

const getOutletAddress = (outlet: ApiOutlet): string =>
  [
    outlet.physical_address_1,
    outlet.physical_address_2,
    outlet.physical_suburb,
    outlet.physical_city,
    outlet.physical_postcode,
    outlet.physical_state,
  ]
    .filter(Boolean)
    .join(', ')

const toOutletOption = (outlet: ApiOutlet): OutletOption => ({
  id: outlet.id,
  name: outlet.name,
  address: getOutletAddress(outlet),
  available: 1, // hardcoded, real data plumbing coming soon
})

export const useCrossLocationFulfilmentOutletOptions = ({
  shouldLoadOutletOptions,
}: UseCrossLocationFulfilmentOutletOptionsParams) => {
  const registerManager = useRegisterManager()
  const [outletOptions, setOutletOptions] = useState<OutletOption[]>([])
  const [isOutletOptionsLoading, setIsOutletOptionsLoading] = useState(false)
  const [selectedOutletId, setSelectedOutletId] = useState('')

  useEffect(() => {
    if (!shouldLoadOutletOptions) {
      setOutletOptions([])
      setSelectedOutletId('')
      setIsOutletOptionsLoading(false)
      return
    }

    let isMounted = true

    setIsOutletOptionsLoading(true)

    outletsStore
      .all()
      .then((allOutlets: ApiOutlet[]) => {
        if (!isMounted) {
          return
        }

        const activeOutletId = registerManager.getActiveOutlet()?.id
        const options = registerManager
          .getEligibleCLFTargetOutlets(allOutlets)
          .filter(outlet => outlet.id !== activeOutletId)
          .map(toOutletOption)

        setOutletOptions(options)
        setSelectedOutletId(currentOutletId =>
          options.some(outlet => outlet.id === currentOutletId)
            ? currentOutletId
            : options[0]?.id ?? ''
        )
      })
      .catch(() => {
        if (isMounted) {
          setOutletOptions([])
          setSelectedOutletId('')
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsOutletOptionsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [registerManager, shouldLoadOutletOptions])

  return {
    isOutletOptionsLoading,
    outletOptions,
    selectedOutletId,
    setSelectedOutletId,
  }
}
